import type { Color, DataVisualization, OptionModelDto } from "@eu.mdotm.api/sphere";
import { AgGridTooltipWrapper, useAgGridRowListener } from "$root/third-party-integrations/ag-grid/ag-grid";
import { coalesce, getThemeCssVars, qualifier } from "$root/utils";
import { CircularProgressBar, Column, Row, Text } from "@mdotm/shadcn/legacy/components";
import { Autocomplete, type AutocompleteHandle, IconButton } from "@mdotm/shadcn/mdotm/components";
import { useRefProxyWithState } from "@mdotm/shadcn/legacy/react-extensions";
import { ForEach } from "@mdotm/utils/react-extra";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import type { ColDef, ColDefField } from "ag-grid-community";
import type { CustomCellEditorProps, CustomTooltipProps } from "ag-grid-react";
import { useEffect, useMemo, useRef } from "react";
import { tagColorMap } from "../../colors";
import { TagBadge } from "./Tag";
import { TagCell } from "./tag-views";
import type { ColumnOverrideParams } from "../../column/common-columns-definition";

export type TagSettings = {
	name: string;
	uuid: string;
	multi?: boolean;
	dataVisualization?: DataVisualization;
};

export type TagOptionsParams = {
	options: Array<Required<OptionModelDto>>;
	isLoading: boolean;
};

function readTagOptionsParams(
	colDef:
		| { cellRendererParams?: unknown; cellEditorParams?: unknown; tooltipComponentParams?: unknown }
		| null
		| undefined,
	key: "cellRendererParams" | "cellEditorParams" | "tooltipComponentParams",
): TagOptionsParams {
	const params = colDef?.[key] as TagOptionsParams | undefined;
	return { options: params?.options ?? [], isLoading: params?.isLoading ?? false };
}

export function tagColDef<T>(
	field: ColDefField<T, Array<string>>,
	meta: TagSettings,
	opt?: Omit<
		ColumnOverrideParams<T, Array<string>>,
		"tooltipComponent" | "cellRenderer" | "cellEditor" | "tooltipValueGetter" | "valueGetter"
	>,
): ColDef<T, Array<string>> {
	return {
		field,
		colId: meta.uuid,
		headerName: meta.name,
		suppressKeyboardEvent: (params) => params.editing,
		tooltipComponent: tooltipComponentWrapper(),
		cellRenderer: cellRendererWrapper(meta),
		cellEditor: cellEditorWrapper(meta),
		editable: opt?.editable ?? true,
		cellEditorPopup: true,
		cellEditorPopupPosition: "under",
		valueFormatter: (params) => (params.value ?? []).join(", "),
		suppressPaste: (params) => {
			return readTagOptionsParams(params.colDef, "cellRendererParams").isLoading;
		},
		valueParser: (params) => {
			const parsed = params.newValue
				.split(",")
				.map((s) => s.trim())
				.filter(Boolean);
			const { options } = readTagOptionsParams(params.colDef, "cellRendererParams");
			if (options.length === 0) {
				return parsed;
			}
			const validOptions = new Set(options.map((o) => o.option));
			return parsed.filter((s) => validOptions.has(s));
		},
		...coalesce(opt, {}),
	};
}

export function tooltipComponentWrapper() {
	return function TooltipComponent<T>(row: CustomTooltipProps<T, Array<string>>): React.JSX.Element | string {
		const { options } = readTagOptionsParams(row.colDef, "tooltipComponentParams");
		useAgGridRowListener(row, "cellEditingStarted", () => row.hideTooltipCallback?.());
		if (row.value == null || row.value.length === 0) {
			return "";
		}
		const value = row.value ?? [];
		return (
			<AgGridTooltipWrapper className="min-w-[224px]">
				<BaseTagList list={options.filter((x) => value.includes(x.option))} />
			</AgGridTooltipWrapper>
		);
	};
}

export function cellRendererWrapper<T>(meta: TagSettings) {
	return function cellRenderer(row: CustomCellEditorProps<T, Array<string>>): React.JSX.Element {
		const { options, isLoading } = readTagOptionsParams(row.colDef, "cellRendererParams");
		if (isLoading) {
			return (
				<Row alignItems="center" justifyContent="center">
					<CircularProgressBar value="indeterminate" outerDiameter={16} innerDiameter={12} />
				</Row>
			);
		}
		const classification = { ...meta, options, dataVisualization: meta.dataVisualization };
		return <TagCell classification={classification} values={row.value?.map((x) => ({ option: x })) ?? []} />;
	};
}

function cellEditorWrapper<T>(meta: TagSettings) {
	const renderTag = (props: { option: string; color: Color }) => {
		return (
			<Row alignItems="center">
				<TagBadge label={props.option} color={tagColorMap[props.color].badgeColor()} />
			</Row>
		);
	};

	return function CellEditor(row: CustomCellEditorProps<T, Array<string>>): React.JSX.Element {
		const { options, isLoading } = readTagOptionsParams(row.colDef, "cellEditorParams");
		const editableValuesRef = useRef<Array<string>>(row.value ?? []);
		const [editableValues, setEditableValues] = useRefProxyWithState<Array<string>>(row.value ?? [], editableValuesRef);
		useAgGridRowListener(row, "cellEditingStopped", () => {
			row.node.setDataValue(meta.uuid, editableValuesRef.current);
		});

		const portalTarget = useMemo(
			() => (row.eGridCell?.closest(".ag-body-viewport") as HTMLElement | null) ?? undefined,
			[row.eGridCell],
		);

		const initialValueRef = useRef(row.value);
		useEffect(() => {
			if ((initialValueRef.current ?? []).length === 0) {
				autocompleteRef.current?.focus({ preventScroll: true });
			}
		}, []);

		const autocompleteRef = useRef<HTMLInputElement | null>(null);
		const autocompleteHandleRef = useRef<AutocompleteHandle | null>(null);

		const autocompleteOptions = useMemo(
			() =>
				options
					.filter((o) => !editableValues.includes(o.option))
					.map((t) => ({ value: t.option, label: t.option ?? "-", children: () => renderTag(t) })),
			[editableValues, options],
		);

		const selectedTagList = options.filter((x) => editableValues.includes(x.option));

		if (isLoading) {
			return (
				<Column alignItems="center" justifyContent="center" className="min-w-[224px] py-4">
					<CircularProgressBar value="indeterminate" outerDiameter={24} innerDiameter={20} />
				</Column>
			);
		}

		return (
			<Column
				gap={16}
				className="bg-white min-w-[224px] rounded-sm border shadow-lg py-2 px-2 overflow-auto max-h-[300px]"
				onKeyDown={(e) => {
					if (e.key === "Escape") {
						e.preventDefault();
						e.stopPropagation();
						row.stopEditing();
					}
				}}
			>
				<Autocomplete
					portalTarget={portalTarget}
					placeholder="Search..."
					innerRef={autocompleteRef}
					value={null}
					options={autocompleteOptions}
					handleRef={autocompleteHandleRef}
					onChange={(value) => {
						if (value != null) {
							setEditableValues((latestSelection) => (meta.multi === false ? [value] : [...latestSelection, value]));
						}
						autocompleteHandleRef.current?.clear();
					}}
					className="focus-within:border-[#DFE2E7]"
					size="x-small"
					triggerDataAttrs={{ "data-qualifier": qualifier.component.autocomplete.trigger(row.node.id!) }}
					data-qualifier={qualifier.component.autocomplete.trigger(row.node.id!)}
				>
					<Text as="div" type="Body/S/Medium" className="py-2 px-2.5">
						{autocompleteOptions.length > 0 ? "Select an option" : "No more tags available"}
					</Text>
				</Autocomplete>
				<Column
					className="overflow-y-auto overflow-x-hidden max-h-[300px]"
					data-qualifier={`EditableTagList/${row.node.id}`}
				>
					<BaseTagList
						list={selectedTagList}
						onRemoveTag={(opt) => setEditableValues((latestSelection) => latestSelection.filter((x) => x !== opt))}
					/>
				</Column>
			</Column>
		);
	};
}

function BaseTagList(props: { list: Array<{ option: string; color: Color }>; onRemoveTag?(option: string): void }) {
	return (
		<Column childrenGrow={0}>
			<ForEach collection={props.list}>
				{(item) => {
					return (
						<Row
							alignItems="center"
							justifyContent="unset"
							className={`border-[color:var(--palette-N50)] border-b border-solid] last:border-none -mx-2 px-2 py-1 last:pb-0`}
						>
							<TagBadge
								label={item.option}
								color={tagColorMap[item.color]?.badgeColor() ?? getThemeCssVars(themeCSSVars.palette_N50)}
							/>
							<Row flexGrow={0} alignItems="center" className="ml-auto">
								{props.onRemoveTag && (
									<IconButton
										icon="Close"
										onClick={() => props.onRemoveTag?.(item.option)}
										size="14"
										color={themeCSSVars.palette_N600}
										className="p-0.5! -m-0.5!"
										data-qualifier={qualifier.component.button(`Delete/Option/${item.option}`)}
									/>
								)}
							</Row>
						</Row>
					);
				}}
			</ForEach>
		</Column>
	);
}
